import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import { useAuth } from '../util/auth';
import './AuthSocial.scss';

// Retry helper function for transient failures
const retryOperation = async (operation, maxRetries = 3, delayMs = 1000) => {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      // Don't retry user-cancelled operations or permission denials
      if (
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request' ||
        error.code === 'auth/user-cancelled'
      ) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * Math.pow(2, attempt)));
      }
    }
  }
  throw lastError;
};

function AuthSocial({ onAuth, onError, providers, buttonAction, showLastUsed }) {
  const auth = useAuth();
  const [pending, setPending] = useState(null);
  const [lastUsed, setLastUsed] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const providerDisplayNames = {
    google: 'Google',
    facebook: 'Facebook',
    twitter: 'Twitter',
    github: 'GitHub',
    microsoft: 'Azure Active Directory'
  };

  // Wait for auth to be ready before showing buttons
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const onSigninWithProvider = async (provider) => {
    setPending(provider);

    try {
      // Retry sign-in operation to handle transient Google API loading failures
      const user = await retryOperation(() => auth.signinWithProvider(provider), 3, 1000);

      localStorage.setItem('lastUsedAuthProvider', provider);
      onAuth(user);
    } catch (error) {
      setPending(null);

      // Provide more helpful error messages for common issues
      let errorMessage = error.message;
      if (error.message && error.message.includes('is not a function')) {
        errorMessage =
          'Authentication service is still loading. Please wait a moment and try again.';
      }

      onError(errorMessage);
    }
  };

  // Get value of last used auth provider
  useEffect(() => {
    if (showLastUsed) {
      const thisLastUsed = window.localStorage.getItem('lastUsedAuthProvider');
      if (thisLastUsed) {
        setLastUsed(thisLastUsed);
      }
    }
  }, [showLastUsed]);

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" role="status" aria-hidden>
          <span className="sr-only">Loading authentication...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {providers.map((provider) => (
        <Button
          variant="light"
          size="lg"
          block
          onClick={() => {
            onSigninWithProvider(provider);
          }}
          className="position-relative"
          key={provider}
          disabled={pending !== null}
        >
          {pending !== provider && (
            <span>
              {buttonAction} with {providerDisplayNames[provider]}
            </span>
          )}

          {pending === provider && (
            <Spinner
              animation="border"
              size="sm"
              role="status"
              aria-hidden
              className="align-baseline text-primary"
            >
              <span className="sr-only">Loading...</span>
            </Spinner>
          )}

          {provider === lastUsed && (
            <Badge
              variant="warning"
              className="position-absolute font-weight-normal"
              style={{
                top: '-6px',
                right: '-6px',
                opacity: 0.7
              }}
            >
              Last used
            </Badge>
          )}
        </Button>
      ))}
    </>
  );
}

AuthSocial.propTypes = {
  onAuth: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  providers: PropTypes.arrayOf(PropTypes.string).isRequired,
  showLastUsed: PropTypes.bool.isRequired,
  buttonAction: PropTypes.string.isRequired
};

export default AuthSocial;
